import {
  add,
  endOfDay,
  getISODay,
  isAfter,
  isBefore,
  isEqual,
  isSameMonth,
  setISODay,
  startOfDay,
  startOfMonth,
} from "date-fns";

export const getQuery = (): URLSearchParams =>
  new URLSearchParams(window.location.search);

const getFirstValidMonday = (day: Date): Date => {
  const dayINeed = 1; // monday
  const today = getISODay(day);

  if (today <= dayINeed) {
    // this week's instance
    return startOfDay(setISODay(day, dayINeed));
  }

  // next week's instance
  return startOfDay(setISODay(add(day, { weeks: 1 }), dayINeed));
};

const getEndOfPeriod = (start: Date): Date =>
  endOfDay(add(add(start, { weeks: 2 }), { days: -1 }));

export const getInitialValidPeriod = (): [Date, Date] => {
  const current = new Date();
  const start = getFirstValidMonday(current);
  const end = getEndOfPeriod(start);

  if (isSameMonth(start, end)) {
    return [start, end];
  }

  const startNextMonth = getFirstValidMonday(
    startOfMonth(add(current, { months: 1 })),
  );
  const endNextMonth = getEndOfPeriod(startNextMonth);

  return [startNextMonth, endNextMonth];
};

export const getChannelName = (abbr: string) => {
  switch (abbr) {
    case "R1":
      return "NPO Radio 1";
    case "R2":
      return "NPO Radio 2";
    case "R3":
      return "NPO 3FM";
    case "R4":
      return "NPO Klassiek";
    case "R5":
      return "NPO Radio 5";
    case "RR":
      return "Regio";
    default:
      break;
  }
  return null;
};

export const getTargetGroupAbbrevation = (targetGroupId: string) =>
  targetGroupId
    .replace("HHV ", "Huishoudverantwoordelijke ")
    .replace("M ", "Mannen ")
    .replace("V ", "Vrouwen ");

export const getTargetGroupName = (
  targetGroupId: string,
  replaceGeneralInterest: boolean = true,
) => {
  switch (targetGroupId) {
    case "BTOB":
      return "Zakelijk";
    case replaceGeneralInterest && "25+":
      return "Algemeen nut";
    case "68+":
      return "Senioren";
    default:
      return `${getTargetGroupAbbrevation(targetGroupId)} jaar`;
  }
};

export const getTargetGroupTooltip = (targetGroupId: string) => {
  switch (targetGroupId.toLocaleLowerCase()) {
    case "hhv 25-67":
      return "Huishoudverantwoordelijke tussen de 25 en 67 jaar. Dit zijn de mensen die binnen een huishouden voor het grootste deel van het huishoudelijk werk zorgen.";
    default:
      return null;
  }
};

export const getPackageTooltip = (packageName: string) => {
  switch (packageName.toLocaleLowerCase()) {
    case "algemeen nut":
      return "Enkel voor niet-commerciële organisaties met een niet-commerciële boodschap. Voorwaarden zijn van toepassing.";
    case "day time":
      return "Ster deelt jouw reclame in voor 18:00 uur.";
    case "day & night time":
      return "Ster deelt jouw reclame in voor 18:00 uur en na 24:00 uur.";
    case "senioren":
      return "Inkoopoptie gericht op mensen van 68 jaar en ouder. Jouw reclame is te zien op NPO 1 tot 18:00 uur en NPO 2 tot 24:00 uur.";
    case "ster time":
      return "Jouw reclame wordt verdeeld over de gehele dag uitgezonden.";
    case "prime time":
      return "Ster deelt jouw reclame in tussen 18:00 en 24:00 uur.";
    case "spits":
      return "Minimaal 5 dagen. Campagne wordt uitgezonden van maandag tot en met vrijdag tussen 06:00 en 09:00 uur en 16:00 en 19:00 uur.";
    default:
      return null;
  }
};

export const isSameOrBeforeDay = (date: Date, dateToCompare: Date) =>
  isBefore(startOfDay(date), startOfDay(dateToCompare)) ||
  isEqual(startOfDay(date), startOfDay(dateToCompare));

export const isSameOrAfterDay = (date: Date, dateToCompare: Date) =>
  isAfter(startOfDay(date), startOfDay(dateToCompare)) ||
  isEqual(startOfDay(date), startOfDay(dateToCompare));
